import { Button } from '@mui/material';
import LoadingIcon from 'components/shared/LoadingIcon/LoadingIcon';
import { useTranslation } from 'react-i18next';
import { ActionsContainer } from '../styles';
import { ContentContainer } from './SmartActionDialog.styles';
import { SmartActionState } from './SmartActionDialog.types';
import { OverlapAction, RecurrenceAction } from './components';

import type {
  SmartAction,
  SmartActionComponentProps
} from './SmartActionDialog.types';

type SmartActionDialogContentProps = {
  canContinue: boolean;
  currentAction: SmartAction;
  isLastAction: boolean;
  isLoading: boolean;
  overlapAction?: SmartAction;
  recurrenceAction?: SmartAction;
  showRecurrence: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onSelect: SmartActionComponentProps['onSelect'];
  setCanContinue: SmartActionComponentProps['setCanContinue'];
};

export const SmartActionDialogContent = ({
  canContinue,
  currentAction,
  isLastAction,
  isLoading,
  overlapAction,
  recurrenceAction,
  showRecurrence,
  onCancel,
  onConfirm,
  onSelect,
  setCanContinue
}: SmartActionDialogContentProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const isConfirmDisabled =
    !canContinue ||
    isLoading ||
    (currentAction.state === SmartActionState.ACTIVE &&
      currentAction.interactionType !== 'informational' &&
      !isLastAction);

  return (
    <ContentContainer>
      {showRecurrence && (
        <RecurrenceAction
          state={recurrenceAction?.state}
          onSelect={onSelect}
          setCanContinue={setCanContinue}
        />
      )}

      <OverlapAction
        state={overlapAction?.state}
        messages={overlapAction?.props?.messages as string[]}
        onSelect={onSelect}
        setCanContinue={setCanContinue}
      />

      <ActionsContainer>
        <Button variant="text" fullWidth onClick={onCancel}>
          {t('dialogs.smartAction.cancel')}
        </Button>

        <Button disabled={isConfirmDisabled} fullWidth onClick={onConfirm}>
          {isLoading ? (
            <LoadingIcon size="SMALL" />
          ) : (
            t('dialogs.smartAction.confirm')
          )}
        </Button>
      </ActionsContainer>
    </ContentContainer>
  );
};
